/*
    Page Name : Temperature Unit Handling
    Purpose   : Branch-wise Celsius / Fahrenheit for vital signs.
                Saudi branch (+966) -> Celsius, every other branch -> Fahrenheit.
    Source    : LoginCountryCode in localStorage, set at login from the
                branch's mobileCountryCode.
*/
import { Injectable } from '@angular/core';
import { ValidatorFn, Validators } from '@angular/forms';

export type TempUnit = 'C' | 'F';

export interface TempRange {
  min: number;
  max: number;
  pattern: string;
  symbol: string;
  maxLength: number;
}

@Injectable({
  providedIn: 'root',
})
export class TemperatureUnitService {

  /** Only this country code gets Celsius. Every other branch stays Fahrenheit. */
  private static readonly CELSIUS_COUNTRY_CODE = '+966';

  /** 86F = 30C and 113F = 45C - same clinical limits, only the unit differs. */
  private static readonly RANGES: { [key: string]: TempRange } = {
    C: { min: 30, max: 45,  pattern: '^[0-9]{1,2}([.][0-9]{1,2})?$', symbol: '°C', maxLength: 5 },
    F: { min: 86, max: 113, pattern: '^[0-9]{1,3}([.][0-9]{1,2})?$', symbol: '°F', maxLength: 6 },
  };

  /** Unit for the branch the user logged into. */
  getUnit(): TempUnit {
    const countryCode = localStorage.getItem('LoginCountryCode');
    return countryCode === TemperatureUnitService.CELSIUS_COUNTRY_CODE ? 'C' : 'F';
  }

  isCelsiusBranch(): boolean {
    return this.getUnit() === 'C';
  }

  getRange(): TempRange {
    return TemperatureUnitService.RANGES[this.getUnit()];
  }

  /** '°C' or '°F' - use for field labels and grid display. */
  getSymbol(): string {
    return this.getRange().symbol;
  }

  /** Validators for a temperature form control. */
  getValidators(): ValidatorFn[] {
    const range = this.getRange();
    return [
      Validators.min(range.min),
      Validators.max(range.max),
      Validators.pattern(range.pattern),
    ];
  }

  /** F <-> C conversion, for trend graphs or reports that mix sources. */
  convert(value: any, from: TempUnit, to: TempUnit): number {
    const num = Number(value);
    if (value === null || value === undefined || value === '' || isNaN(num)) { return null; }
    if (from === to) { return num; }

    const converted = from === 'F'
      ? (num - 32) * 5 / 9      // F -> C
      : (num * 9 / 5) + 32;     // C -> F

    return Math.round(converted * 10) / 10;
  }

  /** Value + unit for read-only display, e.g. "37.2 °C". */
  display(value: any): string {
    if (value === null || value === undefined || value === '') { return '-'; }
    return `${value} ${this.getSymbol()}`;
  }
}
